import { StreamSfuClient } from '../StreamSfuClient';
import { StatsOptions } from '../gen/coordinator';
import { Publisher, Subscriber } from '../rtc';
import { PendingDelta, Tracer, TraceRecord } from './rtc';
import { getSdkName, getSdkVersion } from './utils';
import { getDeviceState, getWebRTCInfo } from '../helpers/client-details';
import { hasPending, withoutConcurrency } from '../helpers/concurrency';
import { timeboxed } from '../coordinator/connection/utils';
import {
  ClientDetails,
  PerformanceStats,
  WebsocketReconnectStrategy,
} from '../gen/video/sfu/models/models';
import { Telemetry } from '../gen/video/sfu/signal_rpc/signal';
import { videoLoggerSystem } from '../logger';

export type SfuStatsReporterOptions = {
  options: StatsOptions;
  clientDetails: ClientDetails;
  subscriber: Subscriber;
  publisher?: Publisher;
  tracer: Tracer;
  unifiedSessionId: string;
};

export class SfuStatsReporter {
  private readonly logger = videoLoggerSystem.getLogger('SfuStatsReporter');

  readonly options: StatsOptions;

  private readonly sfuClient: StreamSfuClient;
  private readonly subscriber: Subscriber;
  private readonly publisher?: Publisher;
  private readonly tracer: Tracer;
  private readonly unifiedSessionId: string;

  private intervalId: NodeJS.Timeout | undefined;
  private timeoutId: NodeJS.Timeout | undefined;
  private reportCount: number = 0;
  private readonly sdkName: string;
  private readonly sdkVersion: string;
  private readonly webRTCVersion: string;
  private readonly statsConcurrencyTag = Symbol('sfuStatsReporter');
  private isStopped = false;

  constructor(
    sfuClient: StreamSfuClient,
    {
      options,
      clientDetails,
      subscriber,
      publisher,
      tracer,
      unifiedSessionId,
    }: SfuStatsReporterOptions,
  ) {
    this.sfuClient = sfuClient;
    this.options = options;
    this.subscriber = subscriber;
    this.publisher = publisher;
    this.tracer = tracer;
    this.unifiedSessionId = unifiedSessionId;

    const { sdk, browser } = clientDetails;
    this.sdkName = getSdkName(sdk);
    this.sdkVersion = getSdkVersion(sdk);

    // use the WebRTC version if set by the SDK (React Native) otherwise,
    // use the browser version as a fallback
    const webRTCInfo = getWebRTCInfo();
    this.webRTCVersion =
      webRTCInfo?.version ||
      `${browser?.name || ''}-${browser?.version || ''}` ||
      'N/A';
  }

  sendConnectionTime = (connectionTimeSeconds: number) => {
    this.sendTelemetryData({
      data: {
        oneofKind: 'connectionTimeSeconds',
        connectionTimeSeconds,
      },
    });
  };

  sendReconnectionTime = (
    strategy: WebsocketReconnectStrategy,
    timeSeconds: number,
  ) => {
    this.sendTelemetryData({
      data: {
        oneofKind: 'reconnection',
        reconnection: { strategy, timeSeconds },
      },
    });
  };

  private sendTelemetryData = (telemetryData: Telemetry) => {
    // intentionally not awaiting the promise here
    // to avoid impeding with the ongoing actions.
    this.run(telemetryData).catch((err) => {
      this.logger.warn('Failed to send telemetry data', err);
    });
  };

  /**
   * Samples both peer connections. Each `StatsTracer.takeSample()` is serialized
   * internally, so this is safe even if it overlaps another sample (e.g. the
   * connection-state-change handler). Kept separate from `send()` so an
   * explicit flush can capture the sample from live peer connections before
   * they are disposed, without waiting for an in-flight send.
   */
  private sample = (): Promise<
    [PerformanceStats[], PerformanceStats[] | undefined]
  > =>
    Promise.all([
      this.subscriber.stats.takeSample(),
      this.publisher?.stats.takeSample(),
    ]);

  private send = (
    subscriberPerfStats: PerformanceStats[],
    publisherPerfStats: PerformanceStats[] | undefined,
    telemetry?: Telemetry,
  ) => {
    // serialize sends so overlapping ones can't race on the trace buffers or
    // deliver an older delta after a newer one already succeeded. Not gated by
    // `isStopped`: an explicit final flush must still deliver after stop().
    return withoutConcurrency(this.statsConcurrencyTag, async () => {
      // The delta chain is delivered only when delta tracing is enabled
      // (the peer-connection tracer exists). Otherwise we drop the chain so it
      // can't grow unbounded.
      const subTracer = this.subscriber.tracer;
      const pubTracer = this.publisher?.tracer;
      let subPending: PendingDelta[] = [];
      if (subTracer) {
        subPending = this.subscriber.stats.getPendingDeltas();
      } else {
        this.subscriber.stats.clearPendingDeltas();
      }
      let pubPending: PendingDelta[] = [];
      if (pubTracer && this.publisher) {
        pubPending = this.publisher.stats.getPendingDeltas();
      } else {
        this.publisher?.stats.clearPendingDeltas();
      }

      const subscriberTrace = subTracer?.take();
      const publisherTrace = pubTracer?.take();
      const tracer = this.tracer.take();
      const sfuTrace = this.sfuClient.getTrace();
      const traces: TraceRecord[] = [
        ...tracer.snapshot,
        ...(sfuTrace?.snapshot ?? []),
        ...(publisherTrace?.snapshot ?? []),
        ...(subscriberTrace?.snapshot ?? []),
        ...toGetStatsRecords(subPending, subTracer?.id),
        ...toGetStatsRecords(pubPending, pubTracer?.id),
      ];

      try {
        const { response } = await this.sfuClient.sendStats({
          sdk: this.sdkName,
          sdkVersion: this.sdkVersion,
          webrtcVersion: this.webRTCVersion,
          rtcStats: JSON.stringify(traces),
          encodeStats: publisherPerfStats ?? [],
          decodeStats: subscriberPerfStats,
          unifiedSessionId: this.unifiedSessionId,
          deviceState: getDeviceState(),
          telemetry,
        });
        // An SFU application-layer error means the stats were not accepted.
        // Treat it like a transport failure: retain the chain and roll back the
        // RTC traces (handled by the catch below) instead of committing.
        if (response?.error) {
          throw new Error(`SFU rejected stats: ${response.error.message}`);
        }
        // delivery confirmed: advance the delivery baseline for each chain.
        if (subTracer) this.subscriber.stats.commitDeltas(subPending);
        if (pubTracer && this.publisher) {
          this.publisher.stats.commitDeltas(pubPending);
        }
      } catch (err) {
        // keep the delta chains (re-sent next interval); only the append-only
        // RTC event traces are rolled back so they aren't lost.
        publisherTrace?.rollback();
        subscriberTrace?.rollback();
        tracer.rollback();
        sfuTrace?.rollback();
        throw err;
      }
    });
  };

  /**
   * Samples and sends one report. Used by the scheduler and the telemetry path.
   * Bails if the reporter has been stopped so it never samples disposed peer
   * connections.
   */
  private run = async (telemetry?: Telemetry) => {
    if (this.isStopped) return;
    const [subscriberPerfStats, publisherPerfStats] = await this.sample();
    await this.send(subscriberPerfStats, publisherPerfStats, telemetry);
  };

  private scheduleNextReport = () => {
    const intervals = [1500, 3000, 3000, 5000];
    if (this.reportCount < intervals.length) {
      this.timeoutId = setTimeout(() => {
        this.scheduledFlush();
        this.reportCount++;
        this.scheduleNextReport();
      }, intervals[this.reportCount]);
    } else {
      clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        this.scheduledFlush();
      }, this.options.reporting_interval_ms);
    }
  };

  start = () => {
    if (this.options.reporting_interval_ms <= 0) return;

    this.isStopped = false;
    this.reportCount = 0;
    clearInterval(this.intervalId);
    clearTimeout(this.timeoutId);

    this.scheduleNextReport();
  };

  stop = () => {
    this.isStopped = true;
    clearInterval(this.intervalId);
    this.intervalId = undefined;
    clearTimeout(this.timeoutId);
    this.timeoutId = undefined;
    this.reportCount = 0;
  };

  /**
   * Explicit/final flush (leave, migration, re-init, page hidden). Time-boxes
   * the sampling step and swallows its failures, so a slow or failing
   * `getStats()` on a degraded or closing peer connection can never block or
   * reject call teardown or reconnect setup. On a successful sample it fires
   * the send best-effort; the returned promise resolves once the sample is
   * taken (or the time-box elapses / sampling fails), never when the sending
   * completes. No-op once the reporter has been stopped.
   */
  flush = async (): Promise<void> => {
    if (this.isStopped) return;
    try {
      const [sample] = await timeboxed([this.sample()], 2000);
      const [subscriberPerfStats, publisherPerfStats] = sample;
      this.send(subscriberPerfStats, publisherPerfStats).catch((err) => {
        this.logger.warn('Failed to flush report stats', err);
      });
    } catch (err) {
      this.logger.warn('Failed to sample stats for the final flush', err);
    }
  };

  /**
   * Flush entry for the periodic scheduler. Skips when the reporter is stopped
   * or a send is already in flight so ticks can't pile up under slow sends (the
   * next sample's delta spans the skipped interval).
   */
  private scheduledFlush = () => {
    if (this.isStopped || hasPending(this.statsConcurrencyTag)) return;
    this.run().catch((err) => {
      this.logger.warn('Failed to flush report stats', err);
    });
  };
}

/**
 * Wraps un-acked, delta-compressed samples into the legacy `getstats` trace
 * record shape so the chain rides inside `rtcStats`, wire-compatible with the
 * server's existing decoder.
 */
const toGetStatsRecords = (
  pending: PendingDelta[],
  id: string | null = null,
): TraceRecord[] => pending.map(({ delta, ts }) => ['getstats', id, delta, ts]);
